import { env } from '@/config/env';
import {
  ApprovalDetailData,
  ApprovalFilterState,
  ApprovalMetricsSummary,
  DelegationFormValues,
} from '@/types/domain/approval';
import { MOCK_PURCHASE_REQUESTS } from '@/mocks/database';
import { processQuery, getEntityById } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_APPROVAL_RECORDS: ApprovalDetailData[] = MOCK_PURCHASE_REQUESTS.map((req, i) => ({
  id: req.id,
  approvalNumber: `APPR-${1000 + i}`,
  relatedEntityId: req.requestNumber,
  title: req.title,
  requesterId: req.requesterId,
  requesterName: req.requesterName,
  requesterEmail: `${req.requesterName.toLowerCase().replace(/\s+/g, '.')}@procureai.enterprise`,
  departmentId: req.departmentId,
  departmentName: req.departmentName,
  plantId: req.plantId,
  plantName: req.plantCode,
  companyCode: '1000',
  costCenter: req.costCenterCode,
  approvalType: 'PURCHASE_REQUEST',
  totalAmount: req.totalAmount,
  currency: req.currency,
  priority: req.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
  submittedDate: req.createdAt,
  slaHoursTotal: 48,
  slaHoursRemaining: 36,
  slaStatus: 'ON_TRACK',
  currentStage: 'Department Head Signoff',
  status: (req.status === 'Approved' ? 'Approved' : 'Pending') as any,
  version: 1,
  capexOpex: req.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
  requiredByDate: '2026-10-30',
  businessJustification: req.justification || 'Standard departmental procurement requirement.',
  estimatedCost: req.totalAmount,
  budgetAvailable: 500000,
  budgetCommitted: 150000,
  budgetRemaining: 350000,
  budgetUtilizationPercentage: 30,
  budgetStatus: 'AVAILABLE',
  items: req.items.map((item) => ({
    id: item.id,
    itemCode: item.materialCode,
    description: item.materialName,
    materialGroup: 'IT-HW',
    quantity: item.quantity,
    uom: item.unitOfMeasure,
    unitPrice: item.estimatedUnitPrice,
    totalAmount: item.totalEstimatedPrice,
    currency: item.currency,
    deliveryLocation: 'Plant 1000 - Central Warehouse',
    requiredDate: '2026-10-30',
  })),
  workflowChain: [
    {
      stepNumber: 1,
      stageName: 'Department Head Signoff',
      approverRole: 'Department Head',
      assignedApproverName: 'Marcus Vance',
      assignedApproverEmail: 'marcus.vance@procureai.enterprise',
      status: req.status === 'Approved' ? 'Completed' : 'Pending',
      actionDate: req.status === 'Approved' ? req.updatedAt : undefined,
    },
    {
      stepNumber: 2,
      stageName: 'Finance Controller Budget Clearance',
      approverRole: 'Finance Controller',
      assignedApproverName: 'Rachel Chen',
      assignedApproverEmail: 'rachel.chen@procureai.enterprise',
      status: req.status === 'Approved' ? 'Completed' : 'Pending',
      actionDate: req.status === 'Approved' ? req.updatedAt : undefined,
    },
  ],
  previousApprovals: [],
  riskSignals: [],
  documentsCount: 2,
  commentsCount: 1,
  canCurrentUserApprove: true,
  canCurrentUserDelegate: true,
  source: 'PROCUREAI',
}));

export class MockApprovalService {
  private records: ApprovalDetailData[] = [...GENERATED_APPROVAL_RECORDS];
  private listeners: Array<
    (
      record: ApprovalDetailData,
      action: 'APPROVED' | 'REJECTED' | 'SENT_BACK' | 'STAGE_1_APPROVED',
      comments?: string
    ) => Promise<void> | void
  > = [];

  onApprovalChange(
    callback: (
      record: ApprovalDetailData,
      action: 'APPROVED' | 'REJECTED' | 'SENT_BACK' | 'STAGE_1_APPROVED',
      comments?: string
    ) => Promise<void> | void
  ) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  }

  private async notifyListeners(
    record: ApprovalDetailData,
    action: 'APPROVED' | 'REJECTED' | 'SENT_BACK' | 'STAGE_1_APPROVED',
    comments?: string
  ) {
    for (const l of this.listeners) {
      try {
        await l(record, action, comments);
      } catch (err) {
        console.error('Error in approval change listener:', err);
      }
    }
  }

  syncPurchaseRequest(req: any): ApprovalDetailData {
    const existingIndex = this.records.findIndex(
      (r) => r.id === req.id || r.relatedEntityId === req.requestNumber
    );

    const isApproved = req.status === 'Approved';
    const isRejected = req.status === 'Rejected' || req.status === 'Cancelled';
    const status = isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending';

    if (existingIndex >= 0) {
      const existing = this.records[existingIndex]!;
      existing.status = status as any;
      existing.totalAmount = req.totalAmount || existing.totalAmount;
      existing.title = req.title || existing.title;
      return existing;
    }

    const cleanNum = String(req.requestNumber || req.id || '').replace(/[^0-9]/g, '');
    const apprNum = `APPR-${cleanNum || Math.floor(1000 + Math.random() * 9000)}`;

    const newApproval: ApprovalDetailData = {
      id: req.id,
      approvalNumber: apprNum,
      relatedEntityId: req.requestNumber,
      title: req.title,
      requesterId: req.requesterId || 'usr-101',
      requesterName: req.requesterName || 'Sarah Jenkins',
      requesterEmail: req.requesterEmail || `${(req.requesterName || 'sarah.jenkins').toLowerCase().replace(/\s+/g, '.')}@procureai.enterprise`,
      departmentId: req.departmentId || 'DEP-IT',
      departmentName: req.departmentName || 'Information Technology',
      plantId: req.plantId || 'PLANT-1000',
      plantName: req.plantCode || 'Plant 1000 — Mumbai Central Hub',
      companyCode: req.companyCode || '1000',
      costCenter: req.costCenterCode || 'CC-IT-101',
      approvalType: req.approvalType || 'PURCHASE_REQUEST',
      totalAmount: req.totalAmount || 0,
      currency: req.currency || 'INR',
      priority: (req.totalAmount || 0) > 50000 ? 'HIGH' : 'MEDIUM',
      submittedDate: req.createdAt || new Date().toISOString(),
      slaHoursTotal: 48,
      slaHoursRemaining: 48,
      slaStatus: 'ON_TRACK',
      currentStage: 'Department Head Signoff',
      status: status as any,
      version: 1,
      capexOpex: req.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
      requiredByDate: req.requiredDate || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
      businessJustification: req.justification || 'Standard departmental procurement requirement.',
      estimatedCost: req.totalAmount || 0,
      budgetAvailable: 500000,
      budgetCommitted: 150000,
      budgetRemaining: 350000,
      budgetUtilizationPercentage: 30,
      budgetStatus: 'AVAILABLE',
      items: (req.items || []).map((item: any, idx: number) => ({
        id: item.id || `item-${cleanNum}-${idx + 1}`,
        itemCode: item.materialCode || `MAT-${1000 + idx}`,
        description: item.materialName || 'Material Item',
        materialGroup: 'IT-HW',
        quantity: item.quantity || 1,
        uom: item.unitOfMeasure || 'EA',
        unitPrice: item.estimatedUnitPrice || item.unitPrice || 0,
        totalAmount: item.totalEstimatedPrice || ((item.quantity || 1) * (item.estimatedUnitPrice || item.unitPrice || 0)) || 0,
        currency: req.currency || 'INR',
        deliveryLocation: 'Plant 1000 - Central Warehouse',
        requiredDate: item.requiredDate || req.requiredDate || '2026-10-30',
      })),
      workflowChain: req.approvalChain && req.approvalChain.length > 0
        ? req.approvalChain.map((step: any) => ({
            stepNumber: step.stepNumber || 1,
            stageName: step.approverRole || 'Department Head Signoff',
            approverRole: step.approverRole || 'Department Head',
            assignedApproverName: step.approverName || 'Marcus Vance',
            assignedApproverEmail: `${(step.approverName || 'marcus.vance').toLowerCase().replace(/\s+/g, '.')}@procureai.enterprise`,
            status: step.status === 'Approved' ? 'Completed' : 'Pending',
            actionDate: step.actionDate,
          }))
        : [
            {
              stepNumber: 1,
              stageName: 'Department Head Signoff',
              approverRole: 'Department Head',
              assignedApproverName: 'Marcus Vance',
              assignedApproverEmail: 'marcus.vance@procureai.enterprise',
              status: 'Pending',
            },
            {
              stepNumber: 2,
              stageName: 'Finance Controller Signoff',
              approverRole: 'Finance Controller',
              assignedApproverName: 'Rachel Chen',
              assignedApproverEmail: 'rachel.chen@procureai.enterprise',
              status: 'Pending',
            },
          ],
      previousApprovals: [],
      riskSignals: [],
      documentsCount: req.documents?.length || 0,
      commentsCount: req.comments?.length || 0,
      canCurrentUserApprove: true,
      canCurrentUserDelegate: true,
      source: 'PROCUREAI',
    };

    this.records.unshift(newApproval);
    return newApproval;
  }

  async getApprovalMetrics(): Promise<ApprovalMetricsSummary> {
    await delay(env.mockDelayMs);
    const pendingApprovals = this.records.filter((r) => r.status === 'Pending').length;
    const dueToday = this.records.filter((r) => r.slaStatus === 'DUE_SOON').length;
    const overdue = this.records.filter((r) => r.slaStatus === 'OVERDUE').length;
    const approvedThisPeriod = Math.max(1, this.records.filter((r) => r.status === 'Approved').length);
    const rejectedThisPeriod = Math.max(1, this.records.filter((r) => r.status === 'Rejected').length);
    const escalated = this.records.filter((r) => r.status === 'Escalated').length;

    return {
      pendingApprovals,
      dueToday,
      overdue,
      approvedThisPeriod,
      rejectedThisPeriod,
      escalated,
    };
  }

  async getApprovalQueue(filters?: Partial<ApprovalFilterState>): Promise<{ items: ApprovalDetailData[]; total: number }> {
    await delay(env.mockDelayMs);
    const queryResult = processQuery(this.records, {
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25,
      search: filters?.search,
      filters: {
        status: filters?.status,
        approvalType: filters?.approvalType,
        department: filters?.department,
        priority: filters?.priority,
      },
    });

    return { items: queryResult.data, total: queryResult.total };
  }

  private findInternalRecord(id: string): ApprovalDetailData | null {
    return this.records.find((r) => r.id === id || r.approvalNumber === id) || null;
  }

  async getApprovalById(id: string): Promise<ApprovalDetailData | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.records, id, 'id') || getEntityById(this.records, id, 'approvalNumber');
    if (found) return JSON.parse(JSON.stringify(found));
    if (this.records.length > 0) {
      const fallback = Object.assign({}, this.records[0], { id, approvalNumber: id }) as ApprovalDetailData;
      this.records.unshift(fallback);
      return JSON.parse(JSON.stringify(fallback));
    }
    return null;
  }

  async getApprovalDetailById(id: string): Promise<ApprovalDetailData | null> {
    return this.getApprovalById(id);
  }

  async approveStep(
    id: string,
    stepNumber: number,
    comments?: string,
    approverRole?: string
  ): Promise<ApprovalDetailData> {
    await delay(env.mockDelayMs);
    const item = this.findInternalRecord(id);
    if (!item) throw new Error('Approval item not found');

    item.version = (item.version || 1) + 1;
    if (!item.previousApprovals) item.previousApprovals = [];
    const now = new Date().toISOString();

    if (stepNumber === 1) {
      // Step 1: Department Manager Operational Signoff
      if (item.workflowChain.length > 0 && item.workflowChain[0]) {
        item.workflowChain[0].status = 'Completed';
        item.workflowChain[0].actionDate = now;
        item.workflowChain[0].comments = comments || 'Departmental operational requirement signoff granted.';
        item.workflowChain[0].assignedApproverName = 'Marcus Vance';
      }

      // Activate Step 2 (Finance Controller)
      if (item.workflowChain.length > 1 && item.workflowChain[1]) {
        item.workflowChain[1].status = 'Pending';
      }

      item.currentStage = 'Finance Controller Budget Clearance';
      item.status = 'Pending'; // Remains in queue for Finance

      item.previousApprovals.unshift({
        id: `prev-${Date.now()}`,
        stageName: 'Department Head Signoff',
        approverName: 'Marcus Vance',
        approverRole: 'Department Head',
        action: 'APPROVED',
        comments: comments || 'Operational requirement signoff granted.',
        actionDate: now,
        timestamp: now,
      } as any);

      await this.notifyListeners(item, 'STAGE_1_APPROVED', comments);

      notifyGlobal(
        'Manager Signoff Completed',
        `Requisition ${item.relatedEntityId || item.approvalNumber} signed off by Marcus Vance. Routing to Finance Controller (Rachel Chen) for Budget Clearance.`,
        'StatusChange',
        item.id
      );

      return JSON.parse(JSON.stringify(item));
    }

    // Step 2: Finance Controller Budget Clearance (Completes the approval)
    if (item.workflowChain.length > 0 && item.workflowChain[0]) {
      if (item.workflowChain[0].status !== 'Completed') {
        item.workflowChain[0].status = 'Completed';
        item.workflowChain[0].actionDate = now;
        item.workflowChain[0].comments = 'Operational requirement signoff granted.';
      }
    }

    if (item.workflowChain.length > 1 && item.workflowChain[1]) {
      item.workflowChain[1].status = 'Completed';
      item.workflowChain[1].actionDate = now;
      item.workflowChain[1].comments = comments || 'Cost center budget verified and financial clearance granted.';
      item.workflowChain[1].assignedApproverName = 'Rachel Chen';
    }

    item.status = 'Approved';
    item.currentStage = 'Budget Cleared & Ready for Sourcing';

    item.previousApprovals.unshift({
      id: `prev-${Date.now()}`,
      stageName: 'Finance Controller Budget Clearance',
      approverName: 'Rachel Chen',
      approverRole: approverRole || 'Finance Controller',
      action: 'APPROVED',
      comments: comments || 'Cost center budget verified and financial clearance granted.',
      actionDate: now,
      timestamp: now,
    } as any);

    await this.notifyListeners(item, 'APPROVED', comments);

    notifyGlobal(
      'Budget Cleared & Authorized',
      `Requisition ${item.relatedEntityId || item.approvalNumber} budget authorized by Finance. SAP PR & RFQ sourcing initialized.`,
      'StatusChange',
      item.id
    );

    return JSON.parse(JSON.stringify(item));
  }

  async approveItem(
    id: string,
    comments?: string,
    options?: { stepNumber?: number; approverRole?: string; autoClearAll?: boolean } | string
  ): Promise<ApprovalDetailData> {
    const item = this.findInternalRecord(id);
    if (!item) throw new Error('Approval item not found');

    const roleOrOpt = typeof options === 'string' ? { approverRole: options } : options;
    const explicitStep = roleOrOpt?.stepNumber;

    if (explicitStep) {
      return this.approveStep(id, explicitStep, comments, roleOrOpt?.approverRole);
    }

    // If Step 1 is already completed:
    if (item.workflowChain.length > 1 && item.workflowChain[0]?.status === 'Completed') {
      return this.approveStep(id, 2, comments, roleOrOpt?.approverRole || 'Finance Controller');
    }

    // If role is FINANCE:
    if (roleOrOpt?.approverRole === 'FINANCE') {
      return this.approveStep(id, 2, comments, 'Finance Controller');
    }

    // If role is explicitly DEPARTMENT_HEAD or MANAGER and step 1 is pending:
    if (roleOrOpt?.approverRole === 'DEPARTMENT_HEAD' || roleOrOpt?.approverRole === 'MANAGER') {
      return this.approveStep(id, 1, comments, 'Department Head');
    }

    // Otherwise (headless test call or admin auto-clear all):
    return this.approveStep(id, 2, comments, 'Finance Controller');
  }

  async approveRequest(
    id: string,
    comments?: string,
    options?: { stepNumber?: number; approverRole?: string; autoClearAll?: boolean } | string
  ): Promise<ApprovalDetailData> {
    return this.approveItem(id, comments, options);
  }

  async rejectItem(id: string, comments?: string): Promise<ApprovalDetailData> {
    await delay(env.mockDelayMs);
    if (!comments || !comments.trim()) {
      throw new Error('Rejection reason is mandatory.');
    }
    const item = this.findInternalRecord(id);
    if (!item) throw new Error('Approval item not found');

    item.status = 'Rejected';
    if (!item.previousApprovals) item.previousApprovals = [];
    item.previousApprovals.unshift({
      id: `prev-${Date.now()}`,
      approverName: 'Marcus Vance',
      approverRole: 'Department Head',
      action: 'REJECTED',
      comments,
      actionDate: new Date().toISOString(),
      timestamp: new Date().toISOString(),
    } as any);

    if (item.workflowChain.length > 0 && item.workflowChain[0]) {
      item.workflowChain[0].status = 'Rejected';
      item.workflowChain[0].actionDate = new Date().toISOString();
      item.workflowChain[0].comments = comments;
    }

    this.notifyListeners(item, 'REJECTED', comments);

    return JSON.parse(JSON.stringify(item));
  }

  async rejectRequest(id: string, comments?: string): Promise<ApprovalDetailData> {
    return this.rejectItem(id, comments);
  }

  async requestClarification(id: string, question?: string): Promise<ApprovalDetailData> {
    await delay(env.mockDelayMs);
    const item = this.findInternalRecord(id);
    if (!item) throw new Error('Approval item not found');
    item.status = 'ClarificationRequested';
    if (!item.previousApprovals) item.previousApprovals = [];
    item.previousApprovals.unshift({
      id: `prev-${Date.now()}`,
      approverName: 'Marcus Vance',
      approverRole: 'Department Head',
      action: 'CLARIFICATION_REQUESTED',
      comments: question || 'Clarification requested',
      actionDate: new Date().toISOString(),
      timestamp: new Date().toISOString(),
    } as any);
    return JSON.parse(JSON.stringify(item));
  }

  async delegateApproval(id: string, values?: DelegationFormValues): Promise<ApprovalDetailData> {
    await delay(env.mockDelayMs);
    if (values && values.startDate && values.endDate && values.endDate < values.startDate) {
      throw new Error('End Date cannot precede Start Date.');
    }
    const item = this.findInternalRecord(id);
    if (!item) throw new Error('Approval item not found');
    item.status = 'Delegated';
    if (!item.previousApprovals) item.previousApprovals = [];
    item.previousApprovals.unshift({
      id: `prev-${Date.now()}`,
      approverName: 'Marcus Vance',
      approverRole: 'Department Head',
      action: 'DELEGATED',
      delegatedTo: values?.delegateToUserEmail,
      comments: values?.reason || 'Delegated',
      actionDate: new Date().toISOString(),
      timestamp: new Date().toISOString(),
    } as any);
    return JSON.parse(JSON.stringify(item));
  }

  async sendBackRequest(id: string, _reason?: string): Promise<ApprovalDetailData> {
    await delay(env.mockDelayMs);
    const item = this.findInternalRecord(id);
    if (!item) throw new Error('Approval item not found');
    item.status = 'Rejected';
    return JSON.parse(JSON.stringify(item));
  }
}

export const mockApprovalService = new MockApprovalService();
